import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Fade, InputAdornment, Popper } from '@mui/material'
import { CalendarDot } from '@phosphor-icons/react'
import Card from 'Components/UI/Card'
import { DatePicker } from 'Components/UI/DatePicker'
import Caption from 'Components/UI/Forms/Caption'
import { DateTime } from 'luxon'

import { useOnClickOutside } from 'Hooks'

import { Container, StyledInput, Wrapper, WrapperProps } from './styles'

type Props = WrapperProps & {
  caption?: string
  danger?: boolean
  disabled?: boolean
  label?: ReactNode
  success?: boolean
  value?: Date
  onChange?: (value: Date) => void
}

function DateInput({
  caption,
  danger,
  disabled,
  label,
  success,
  value,
  onChange,
  ...rest
}: Props) {
  const [popperOpen, setPopperOpen] = useState(false)
  const [date, setDate] = useState<Date>()

  useEffect(() => {
    if (value) {
      setDate(value)
    }
  }, [value])

  const inputRef = useRef(null)
  const popperRef = useRef(null)

  const handleOpenerClose = useCallback(() => {
    setPopperOpen(false)
  }, [])

  const handleOpenerOpen = useCallback(() => {
    setPopperOpen(true)
  }, [])

  const handleFinish = useCallback(
    (pickedDate: Date) => {
      onChange?.(pickedDate)

      setDate(pickedDate)
      handleOpenerClose()
    },
    [handleOpenerClose, onChange],
  )

  const formattedValue = useMemo(() => {
    if (!date) {
      return ''
    }

    return DateTime.fromJSDate(date).toLocaleString()
  }, [date])

  useOnClickOutside(popperRef, handleOpenerClose)

  return (
    <Wrapper {...rest}>
      <Container>
        <StyledInput
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarDot />
              </InputAdornment>
            ),
          }}
          disabled={disabled}
          error={danger}
          label={label}
          ref={inputRef}
          value={formattedValue}
          onClick={handleOpenerOpen}
        />

        <Popper
          anchorEl={inputRef.current}
          open={popperOpen}
          placement="bottom"
          ref={popperRef}
          style={{
            zIndex: 10000,
          }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={250}>
              <Card>
                <DatePicker {...rest} value={date} onChange={handleFinish} />
              </Card>
            </Fade>
          )}
        </Popper>
      </Container>
      <Caption caption={caption} danger={danger} mt={2} success={success} />
    </Wrapper>
  )
}

export default DateInput
